import { forwardRef } from 'react';
import type { SpecialEventDetail, SpecialEventTeam } from '@/lib/api';
import { formatPower } from '@/lib/utils';

function playerLabel(name: string, display: string | null) {
  return (display?.trim() || name).trim();
}

const CARD_WIDTH = 1080;

export function SpecialEventTeamCard({
  event,
  team,
  innerRef,
}: {
  event: SpecialEventDetail['event'];
  team: SpecialEventTeam;
  innerRef?: React.Ref<HTMLDivElement>;
}) {
  const rows = team.assignments
    .filter((a) => a.player)
    .slice()
    .sort(
      (a, b) => (b.player!.totalPower ?? 0) - (a.player!.totalPower ?? 0),
    );
  const leaderLabel = team.leader
    ? playerLabel(team.leader.name, team.leader.displayName)
    : '—';
  return (
    <div
      ref={innerRef}
      style={{
        width: CARD_WIDTH,
        padding: 32,
        background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)',
        color: '#e2e8f0',
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        borderRadius: 16,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
        <div style={{ fontSize: 32, fontWeight: 700 }}>
          {team.name?.trim() || `Team ${team.teamIndex}`}
        </div>
        <div style={{ fontSize: 20, color: '#94a3b8', flex: 1 }}>
          {event.customName ?? 'Special event'} · {event.eventDate}
          {event.startsAt ? ` · ${event.startsAt}` : ''}
        </div>
        <div
          style={{
            padding: '4px 10px',
            borderRadius: 8,
            background: 'rgba(99,102,241,0.25)',
            color: '#c7d2fe',
            fontSize: 16,
            fontWeight: 600,
          }}
        >
          {team.totals.assigned}/{team.capacity} · {Math.round(team.strengthShareNormalized * 100)}%
        </div>
      </div>
      <div style={{ marginTop: 8, fontSize: 18, color: '#cbd5e1' }}>
        Leader: <strong style={{ color: '#f8fafc' }}>{leaderLabel}</strong>
      </div>
      {team.task && team.task.trim() !== '' && (
        <div
          style={{
            marginTop: 16,
            padding: '12px 16px',
            borderRadius: 12,
            background: 'rgba(217,70,239,0.15)',
            border: '1px solid rgba(217,70,239,0.4)',
            color: '#f0abfc',
            fontSize: 20,
            fontWeight: 500,
          }}
        >
          <span style={{ fontSize: 14, letterSpacing: 2, marginRight: 12, textTransform: 'uppercase' }}>
            Task
          </span>
          <span style={{ color: '#fdf4ff' }}>{team.task}</span>
        </div>
      )}
      <div
        style={{
          marginTop: 16,
          display: 'flex',
          gap: 24,
          fontSize: 18,
          color: '#cbd5e1',
        }}
      >
        <div>
          Σ M1 <strong style={{ color: '#f8fafc' }}>{formatPower(team.totals.sumM1)}</strong>
        </div>
        <div>
          Σ Total <strong style={{ color: '#f8fafc' }}>{formatPower(team.totals.sumTotal)}</strong>
        </div>
        <div style={{ marginLeft: 'auto', color: '#94a3b8' }}>
          Target Σ Total {formatPower(team.totals.targetTotal)}
        </div>
      </div>
      <table
        style={{
          width: '100%',
          marginTop: 20,
          borderCollapse: 'collapse',
          fontSize: 18,
        }}
      >
        <thead>
          <tr style={{ color: '#64748b', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase' }}>
            <th style={{ textAlign: 'left', padding: '6px 8px', width: 48 }}>#</th>
            <th style={{ textAlign: 'left', padding: '6px 8px' }}>Player</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', width: 80 }}>Rank</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', width: 120 }}>M1</th>
            <th style={{ textAlign: 'right', padding: '6px 8px', width: 140 }}>Total</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a, i) => (
            <tr
              key={a.id}
              style={{ borderTop: '1px solid rgba(148,163,184,0.15)' }}
            >
              <td style={{ padding: '6px 8px', color: '#64748b' }}>{i + 1}</td>
              <td style={{ padding: '6px 8px' }}>
                {playerLabel(a.player!.name, a.player!.displayName)}
                {team.leaderPlayerId === a.playerId && (
                  <span
                    style={{
                      marginLeft: 8,
                      fontSize: 12,
                      padding: '2px 6px',
                      background: 'rgba(250,204,21,0.2)',
                      color: '#fde68a',
                      borderRadius: 4,
                    }}
                  >
                    LEAD
                  </span>
                )}
              </td>
              <td style={{ padding: '6px 8px', color: '#94a3b8' }}>
                {a.player!.rank ?? ''}
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatPower(a.player!.m1Power ?? 0)}
              </td>
              <td style={{ padding: '6px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {formatPower(a.player!.totalPower ?? 0)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Decide whether a team should be split into two sub-columns in the overview.
 * Rule: capacity >= 1.5 × median(capacity of the other teams) AND capacity > 25.
 * 40-20-20-20 → T1 splits. 34-33-33 → nothing splits.
 */
function shouldSplit(team: SpecialEventTeam, all: SpecialEventTeam[]): boolean {
  if (team.capacity <= 25) return false;
  const others = all.filter((t) => t.id !== team.id).map((t) => t.capacity);
  if (others.length === 0) return false;
  const sorted = [...others].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  const median =
    sorted.length % 2 === 0 ? (sorted[mid - 1] + sorted[mid]) / 2 : sorted[mid];
  return median > 0 && team.capacity >= median * 1.5;
}

const SLOT_COLUMN_WIDTH = 220;
const SLOT_GAP = 12;
const TEAM_PADDING = 12;

function SlotColumn({ rows }: { rows: SpecialEventTeam['assignments'] }) {
  return (
    <table
      style={{
        width: SLOT_COLUMN_WIDTH,
        borderCollapse: 'collapse',
        fontSize: 12,
      }}
    >
      <tbody>
        {rows.map((a, i) => (
          <tr key={a.id} style={{ borderTop: '1px solid rgba(148,163,184,0.1)' }}>
            <td style={{ padding: '3px 4px', color: '#64748b', width: 26 }}>
              {(a as { _rank?: number })._rank ?? i + 1}
            </td>
            <td style={{ padding: '3px 4px' }}>
              {playerLabel(a.player!.name, a.player!.displayName)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TeamContainer({ team, split }: { team: SpecialEventTeam; split: boolean }) {
  const rows = team.assignments
    .filter((a) => a.player)
    .slice()
    .sort(
      (a, b) => (b.player!.totalPower ?? 0) - (a.player!.totalPower ?? 0),
    )
    .map((a, i) => ({ ...a, _rank: i + 1 })) as SpecialEventTeam['assignments'];
  const half1 = Math.ceil(rows.length / 2);
  const headerLabel = team.name?.trim() || `Team ${team.teamIndex}`;
  const columns = split
    ? [rows.slice(0, half1), rows.slice(half1)]
    : [rows];
  const width =
    TEAM_PADDING * 2 +
    columns.length * SLOT_COLUMN_WIDTH +
    (columns.length - 1) * SLOT_GAP;
  return (
    <div
      style={{
        flex: '0 0 auto',
        width,
        padding: TEAM_PADDING,
        background: 'rgba(15,23,42,0.7)',
        borderRadius: 12,
        border: '1px solid rgba(148,163,184,0.15)',
        color: '#e2e8f0',
      }}
    >
      <div style={{ fontSize: 20, fontWeight: 700 }}>{headerLabel}</div>
      {team.leader && (
        <div style={{ fontSize: 12, color: '#cbd5e1', marginTop: 2 }}>
          Lead:{' '}
          <strong style={{ color: '#f8fafc' }}>
            {playerLabel(team.leader.name, team.leader.displayName)}
          </strong>
        </div>
      )}
      {team.task && team.task.trim() !== '' && (
        <div
          style={{
            marginTop: 6,
            padding: '4px 8px',
            borderRadius: 6,
            background: 'rgba(217,70,239,0.15)',
            border: '1px solid rgba(217,70,239,0.4)',
            color: '#f0abfc',
            fontSize: 12,
          }}
        >
          {team.task}
        </div>
      )}
      <div style={{ display: 'flex', gap: SLOT_GAP, marginTop: 10 }}>
        {columns.map((c, i) => (
          <SlotColumn key={i} rows={c} />
        ))}
      </div>
    </div>
  );
}

export const SpecialEventOverview = forwardRef<
  HTMLDivElement,
  { data: SpecialEventDetail; innerRef?: React.Ref<HTMLDivElement> }
>(function SpecialEventOverview({ data, innerRef }, _fwdRef) {
  const teamContainers = data.teams.map((t) => ({
    team: t,
    split: shouldSplit(t, data.teams),
  }));
  // Container widths depend on split; pre-compute for the total canvas.
  const containerWidths = teamContainers.map(({ split }) =>
    TEAM_PADDING * 2 +
    (split ? 2 * SLOT_COLUMN_WIDTH + SLOT_GAP : SLOT_COLUMN_WIDTH),
  );
  const width =
    48 +
    containerWidths.reduce((s, w) => s + w, 0) +
    Math.max(0, teamContainers.length - 1) * 16;
  return (
    <div
      ref={innerRef}
      style={{
        width,
        minWidth: 720,
        padding: 24,
        background: 'linear-gradient(180deg, #0f172a 0%, #020617 100%)',
        color: '#e2e8f0',
        fontFamily:
          'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
        borderRadius: 16,
        boxSizing: 'border-box',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 16 }}>
        <div style={{ fontSize: 28, fontWeight: 700 }}>
          {data.event.customName ?? 'Special event'}
        </div>
        <div style={{ fontSize: 18, color: '#94a3b8' }}>
          {data.event.eventDate}
          {data.event.startsAt ? ` · ${data.event.startsAt}` : ''}
        </div>
      </div>
      <div
        style={{
          marginTop: 20,
          display: 'flex',
          gap: 16,
        }}
      >
        {teamContainers.map(({ team, split }) => (
          <TeamContainer key={team.id} team={team} split={split} />
        ))}
      </div>
    </div>
  );
});
